/**
 * Küçük, elle derlenmiş Türkçe sözcük varlığı.
 *
 * Bu bir sözlük DEĞİL ve olmaya çalışmıyor. İki işi var:
 *
 * 1. **Kelime merdiveni** (`word_ladder`) buradan üretiliyor. Merdiven
 *    bir çizge üzerinde yürüyor: düğümler kelimeler, kenarlar "tek harf
 *    farkı". Çizge üretim anında kuruluyor, elle yazılmıyor — yani bir
 *    kelime eklendiğinde yeni yollar kendiliğinden açılıyor.
 *
 * 2. **Kurallı kelime üretme** (`word_fluency`) için TANIK. Yönerge
 *    "içinde E geçmeyen, dört harfli nesne yaz" diyorsa o kuralın
 *    cevabı olduğunu bilmemiz gerekiyor; cevabı olmayan bir yönerge
 *    çocuğu kendi yetersizliğine inandırıyor. Üretim, kuralı sağlayan
 *    en az üç tanık bulamazsa o kuralı atıyor.
 *
 * Tanık listesi PUANLAMAYA GİRMİYOR. Çocuk "kapı" yazdığında listede
 * yoksa yanlış saymak, listenin eksikliğini çocuğun hatası gibi
 * göstermek olurdu — bu yüzden `word_fluency` elle puanlanıyor
 * (`grading: 'manual'`). Yazılım yalnızca BİÇİMİ denetliyor: harf
 * sayısı, yasak harf, baş harf. "Gerçek bir kelime mi, nesne mi"
 * sorusunu uygulayıcı cevaplıyor.
 *
 * Merdiven tarafında ise liste bağlayıcı: çocuğun yazdığı ara kelime
 * listede olmak zorunda, çünkü "tek harf değiştirerek anlamlı kelime"
 * iddiasını başka hiçbir şeyle denetleyemiyoruz. Yönergede bu yüzden
 * havuz görünüyor — kapalı bir kümeden seçim yapmak, sonsuz bir
 * havuzdan tahmin etmekten hem daha dürüst hem daha öğretici.
 */

/**
 * Dört harfli kelimeler.
 *
 * Kümeler bilinçli: "tek harf farkı" komşuluğu seyrek bir ilişki, rastgele
 * toplanmış kelimelerle çizge kopuk kalıyor ve hiç merdiven bulunamıyor.
 * Aşağıdaki öbeklerin her biri kendi içinde bağlı.
 */
export const DORT_HARF = [
  // a_a öbeği — en bağlı küme
  'kara', 'kaya', 'kaza', 'kasa', 'kama', 'masa', 'maya', 'yasa', 'yaka',
  'yara', 'para',
  // e_i / e_e öbeği
  'deri', 'dere', 'ders', 'seri', 'sera', 'geri', 'gemi', 'peri',
  // kedi öbeği
  'kedi', 'yedi', 'yeni',
  // u/o öbeği
  'kuzu', 'kuru', 'kutu', 'kuyu', 'koru', 'boru',
  // hava öbeği
  'tava', 'hava', 'hala', 'hale', 'halı',
  // soba öbeği
  'soba', 'soya', 'boya',
] as const

/**
 * Tanık havuzu: `word_fluency` kurallarının cevaplanabilir olduğunu
 * gösteren kelimeler. Uzunluk çeşitliliği kasıtlı.
 */
export const NESNELER = [
  'kalem', 'kitap', 'masa', 'pencere', 'bahçe', 'deniz', 'bulut', 'orman',
  'köprü', 'yıldız', 'çiçek', 'sandalye', 'bardak', 'anahtar', 'tren',
  'balık', 'ayna', 'defter', 'sepet', 'yaprak', 'okul', 'kapı', 'kutu',
  'soba', 'boru', 'tava', 'kaşık', 'çatal', 'tabak', 'perde', 'halı',
  'lamba', 'duvar', 'merdiven', 'sandık', 'kova', 'ip', 'tuz', 'süt',
  'ekmek', 'peynir', 'zeytin', 'armut', 'elma', 'kiraz', 'erik', 'üzüm',
  'kedi', 'köpek', 'tavşan', 'kartal', 'balina', 'inek', 'koyun', 'tavuk',
  'otobüs', 'bisiklet', 'vapur', 'uçak', 'kamyon', 'gemi', 'araba',
  'gömlek', 'pantolon', 'çorap', 'şapka', 'ceket', 'ayakkabı', 'kazak',
] as const

/** Tek harf farkıyla komşu mu: aynı uzunluk, tam bir konumda ayrım. */
export function tekHarfFarki(a: string, b: string): boolean {
  if (a.length !== b.length) return false
  let fark = 0
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) fark++
    if (fark > 1) return false
  }
  return fark === 1
}

/** Komşuluk çizgesi: kelime → tek harf farklı kelimeler. */
export function merdivenCizgesi(kelimeler: readonly string[]): Map<string, string[]> {
  const g = new Map<string, string[]>()
  for (const a of kelimeler) g.set(a, [])
  for (const a of kelimeler) {
    for (const b of kelimeler) {
      if (a !== b && tekHarfFarki(a, b)) g.get(a)!.push(b)
    }
  }
  return g
}
